import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import MovieCard from "@/components/base/MovieCard";
import { movies } from "@/mocks/movies";
import { news } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(initial);
  const query = (searchParams.get("q") || "").trim().toLowerCase();

  usePageMeta(
    query ? `“${query}”的搜索结果 - 80s电影` : "搜索 - 80s电影",
    "在80s电影站内搜索经典电影与影视资讯内容。"
  );

  const movieResults = useMemo(() => {
    if (!query) return [];
    return movies.filter(
      (m) =>
        m.title.toLowerCase().includes(query) ||
        m.originalTitle.toLowerCase().includes(query) ||
        m.director.toLowerCase().includes(query) ||
        m.cast.some((c) => c.toLowerCase().includes(query)) ||
        m.genres.some((g) => g.toLowerCase().includes(query))
    );
  }, [query]);

  const newsResults = useMemo(() => {
    if (!query) return [];
    return news.filter(
      (n) =>
        n.title.toLowerCase().includes(query) ||
        n.summary.toLowerCase().includes(query)
    );
  }, [query]);

  const hasResults = movieResults.length > 0 || newsResults.length > 0;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setSearchParams(q ? { q } : {});
  };

  return (
    <PageLayout>
      <div className="mx-auto w-full max-w-5xl px-4 py-10 md:px-6">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          搜索
        </h1>
        <form
          onSubmit={handleSubmit}
          className="mt-5 flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3"
        >
          <i className="ri-search-line text-foreground-500"></i>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索电影标题、演员、导演或资讯关键词"
            className="w-full bg-transparent font-label text-sm outline-none placeholder:text-foreground-400"
          />
          <button
            type="submit"
            className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>

        {!query ? (
          <p className="mt-8 font-label text-sm text-foreground-500">
            输入关键词，搜索80年代经典电影或影视资讯
          </p>
        ) : !hasResults ? (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <i className="ri-emotion-sad-line mb-3 flex h-12 w-12 items-center justify-center text-3xl text-foreground-300"></i>
            <p className="font-label text-sm text-foreground-600">
              没有找到与“{query}”相关的内容
            </p>
            <p className="mt-2 font-label text-xs text-foreground-500">
              不妨看看下面这些经典推荐
            </p>
            <div className="mt-6 grid w-full grid-cols-2 gap-4 px-4 sm:grid-cols-4">
              {movies.slice(0, 4).map((movie, index) => (
                <MovieCard key={movie.id} movie={movie} index={index} />
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-8 space-y-10">
            {movieResults.length > 0 && (
              <section>
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  电影（{movieResults.length}）
                </h2>
                <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {movieResults.map((movie, index) => (
                    <MovieCard key={movie.id} movie={movie} index={index} />
                  ))}
                </div>
              </section>
            )}

            {newsResults.length > 0 && (
              <section>
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  影视资讯（{newsResults.length}）
                </h2>
                <div className="mt-4 divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                  {newsResults.map((item) => (
                    <Link
                      key={item.id}
                      to={`/news/${item.id}`}
                      className="flex items-center justify-between gap-3 p-4 transition-colors hover:bg-background-100"
                    >
                      <div className="min-w-0">
                        <p className="font-label text-sm font-semibold text-foreground-900 line-clamp-1">
                          {item.title}
                        </p>
                        <p className="mt-1 font-label text-xs text-foreground-500 line-clamp-1">
                          {item.summary}
                        </p>
                      </div>
                      <i className="ri-arrow-right-s-line shrink-0 text-foreground-400"></i>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </PageLayout>
  );
}